/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TPendingTelemetryLog } from "../types";
import { TelemetryLogType } from "../types";
import { Log } from "./Log";
import { Sage } from "./Sage";
import { PendingTelemetryStore } from "./PendingTelemetryStore";

const TAG: string = "PendingTelemetryFlusher";

const DEFAULT_SEVERITY: number = 1;

/**
 * Reads pending telemetry from IndexedDB and forwards each entry to the Sage
 * stream it was queued for, in FIFO order. An entry is deleted only after a
 * successful send.
 *
 * At-least-once delivery: if the app crashes between the Sage accept and the
 * IndexedDB delete, the entry may be re-sent on the next flush. This is an
 * accepted trade-off for telemetry systems.
 */
export class PendingTelemetryFlusher {
  private static mIsFlushInProgress: boolean = false;

  public static async flush(): Promise<void> {
    if (PendingTelemetryFlusher.mIsFlushInProgress) {
      Log.d(TAG, "flush(): Already in progress — skipping");
      return;
    }
    PendingTelemetryFlusher.mIsFlushInProgress = true;

    try {
      const pending: TPendingTelemetryLog[] | null =
        await PendingTelemetryStore.readAll();
      if (pending === null || pending.length === 0) {
        Log.d(TAG, "flush(): No pending telemetry");
        return;
      }

      const sorted: TPendingTelemetryLog[] = [...pending].sort(
        (a: TPendingTelemetryLog, b: TPendingTelemetryLog) =>
          a.sequence - b.sequence
      );

      const flushedSequences: number[] = [];

      for (const entry of sorted) {
        try {
          PendingTelemetryFlusher.send(entry);
          flushedSequences.push(entry.sequence);
        } catch (sendError: unknown) {
          // Stop at the first failure so the remaining entries keep their
          // order for the next flush.
          Log.e(
            TAG,
            "flush(): Failed to send entry seq=" + String(entry.sequence),
            sendError
          );
          break;
        }
      }

      if (flushedSequences.length > 0) {
        await PendingTelemetryStore.removeBySequences(flushedSequences);
        Log.i(
          TAG,
          "flush(): Flushed " + String(flushedSequences.length) + " entry(s)"
        );
      }
    } catch (error: unknown) {
      Log.e(TAG, "flush()", error);
    } finally {
      PendingTelemetryFlusher.mIsFlushInProgress = false;
    }
  }

  /** Routes one queued entry back onto the stream it came from. */
  private static send(entry: TPendingTelemetryLog): void {
    if (entry.logType === TelemetryLogType.Operational) {
      Sage.logOperations(entry.name, entry.details);
      return;
    }
    if (entry.logType === TelemetryLogType.Analytics) {
      Sage.logAnalytics(entry.name, entry.eventType, entry.details);
      return;
    }
    Sage.logDiagnostics(
      entry.name,
      entry.message === undefined ? "" : entry.message,
      entry.severity === undefined ? DEFAULT_SEVERITY : entry.severity,
      entry.details
    );
  }

  /** Reset static state — test-only. */
  public static resetForTest(): void {
    PendingTelemetryFlusher.mIsFlushInProgress = false;
  }
}
